import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import {Box, Grid, Link, Skeleton, Typography} from '@mui/material';

import Avatar from '@jobseeker/lotus/components/Avatar';
import jobDetailService from '@jobseeker/common-core/services/jobdetail.service';
import {getDiversityCdnUrl} from '@jobseeker/common-core/utils';

import {Image} from '@jobseeker/lotus/components/Image';
import {
  Quotes,
  DiversityLogo,
  ChevronRight,
} from '@jobseeker/lotus/assets/icons';

import {useIsCustomWidth} from '@jobseeker/lotus/hooks/common';
import {getOldLink} from '@jobseeker/lotus/utils';
import Expandable from '@jobseeker/lotus/components/Expandable';

import type {JobDetail as JB} from '@jobseeker/common-core/stateModels/job/jobdetail.models';
import type {
  AppState,
  CompanyDetails,
  Perk,
  Testimonial,
} from '@jobseeker/common-core/types';

const Diversity = () => {
  const [perksAndBenefits, setPerksAndBenefits] = useState<Perk[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [loading, setLoading] = useState(true);
  const isSmall = useIsCustomWidth(500);
  const jobInfo: JB | any = useSelector((state: AppState) => state.jobDetail);
  const diversityId = jobInfo?.diversity?.diversityId;
  const centralizedCompanyId = jobInfo?.diversity?.centralizedCompanyId;
  const [expanded, setExpanded] = useState(false);
  const showPerks = isSmall ? 3 : 4;

  const toggleExpanded = () => setExpanded(!expanded);
  useEffect(() => {
    const fetchPerksAndBenefits = async () => {
      try {
        if (centralizedCompanyId) {
          const perksBenefits = (await jobDetailService.getPerksAndBenefits({
            companyId: centralizedCompanyId,
          })) as CompanyDetails;
          setPerksAndBenefits(perksBenefits?.perksBenefit);
          setTestimonials(perksBenefits?.testimonials);
        }
      } catch (error) {
        console.error('Failed to fetch perks and benefits:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPerksAndBenefits();
  }, [centralizedCompanyId]);

  const visiblePerks = expanded
    ? perksAndBenefits ?? []
    : perksAndBenefits?.slice(0, showPerks) ?? [];

  const Shimmer = () => (
    <Grid
      item
      xs={6}
      md={4}
      lg={3}
      xl={2.4}
      sx={{
        display: 'flex',
        flexDirection: {xxs: 'column', sm: 'row'},
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Skeleton variant="circular" width={40} height={40} />

      <Box sx={{width: '100%'}}>
        <Skeleton
          variant="text"
          height={20}
          sx={{
            marginBottom: 1,
          }}
        />
        <Skeleton variant="text" height={16} />
      </Box>
    </Grid>
  );
  return (
    (jobInfo?.diversity || (diversityId && perksAndBenefits?.length > 0)) && (
      <Box
        mt={3}
        mb={5}
        sx={{position: 'relative'}}
        border={'1px solid #E8ECFA'}
        borderRadius={6}
        width={'100%'}
      >
        {jobInfo?.diversity && testimonials && testimonials.length > 0 && (
          <Box>
            <Box
              mt={4.75}
              zIndex={2}
              display={'flex'}
              width={'100%'}
              pr={4}
              alignItems={'flex-start'}
              sx={{
                position: 'absolute',
                left: {xxs: '50%', sm: 32},
                top: {xxs: 30, sm: -15},
                ...(isSmall && {
                  transform: 'translateX(-12%)',
                }),
              }}
            >
              {loading ? (
                <Skeleton
                  variant="circular"
                  width={isSmall ? 60 : 80}
                  height={isSmall ? 60 : 80}
                />
              ) : (
                <Box
                  sx={{
                    display: 'inline-block',
                    borderRadius: '50%',
                    padding: '2px',
                    background:
                      'linear-gradient(90deg, #4DA3E1 2.57%, #214FA0 24.96%, #EB633E 71.19%, #F1C659 99.91%)',
                  }}
                >
                  <Avatar
                    sx={{
                      width: isSmall ? 60 : 80,
                      height: isSmall ? 60 : 80,
                      borderRadius: '50%',
                      backgroundColor: '#fff',
                    }}
                    imgUrl={
                      testimonials && testimonials.length > 0
                        ? testimonials[0]?.image
                        : `${getOldLink('/resources/img/user.jpg')}`
                    }
                  />
                </Box>
              )}
            </Box>

            <Box position="relative" zIndex={1} mx={{xxs: 1, sm: 2}}>
              <Box
                pl={{xxs: 0, sm: 13}}
                mt={{xxs: 3, lg: 5}}
                sx={{...(!isSmall && {gap: 2.5})}}
                display={'flex'}
                justifyContent={{
                  xxs: 'center',
                  sm: 'space-between',
                }}
                alignItems={'center'}
                width={'100%'}
              >
                {!isSmall && (
                  <Box
                    display={'flex'}
                    flexDirection={'column'}
                    alignItems={'flex-start'}
                  >
                    <Typography
                      variant="body2"
                      fontStyle="italic"
                      lineHeight={'16px'}
                    >
                      {`Hear from the leaders of ${
                        jobInfo?.companyData?.companyName || 'our company'
                      }!`}
                    </Typography>
                    {loading ? (
                      <Skeleton variant="text" width={120} height={16} />
                    ) : (
                      <Typography variant="subtitle3" lineHeight={'16px'}>
                        {(testimonials?.[0]?.name || 'Anonymous').replace(
                          /<br>/g,
                          ' '
                        )}
                      </Typography>
                    )}
                  </Box>
                )}

                <Box
                  display={'flex'}
                  gap={0.5}
                  justifyContent={'center'}
                  alignItems={'center'}
                  sx={{
                    ...(isSmall && {
                      mb: 4,
                    }),
                  }}
                >
                  <Image
                    src={DiversityLogo}
                    alt="diversityLogo"
                    width={24}
                    height={24}
                  />
                  <Typography
                    variant="subtitle2"
                    fontWeight={600}
                    lineHeight="18px"
                    sx={{
                      background:
                        'linear-gradient(90deg, #4DA3E1 2.57%, #214FA0 24.96%, #EB633E 71.19%, #F1C659 99.91%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    DIVERSE EMPLOYER
                  </Typography>
                </Box>
              </Box>
              <Box
                mt={{xxs: 3, sm: 0.75}}
                py={{xxs: 1, sm: 2}}
                pr={{xxs: 1, sm: 2.25}}
                pl={{xxs: 1, sm: 13}}
                sx={{
                  display: 'flex',
                  background: '#E1EAF9',
                  borderRadius: '24px 24px 72px 56px',
                  alignItems: 'flex-start',
                  gap: '8px',
                  transition: 'height 0.3s ease',
                }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  sx={{
                    ...(isSmall && {
                      alignItems: 'center',
                      textAlign: 'center',
                    }),
                  }}
                >
                  {isSmall && (
                    <Box
                      width="100%"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      justifyContent="center"
                      mt={3}
                    >
                      <Typography variant="body3" lineHeight="16px">
                        {`Hear from the leaders of ${
                          jobInfo?.companyData?.companyName || 'our company'
                        }!`}
                      </Typography>
                      <Typography
                        variant="subtitle3"
                        lineHeight="16px"
                        mt={0.25}
                      >
                        {(testimonials?.[0]?.name || 'Anonymous').replace(
                          /<br>/g,
                          ' '
                        )}
                      </Typography>
                    </Box>
                  )}
                  <Box
                    width={'100%'}
                    position="relative"
                    sx={{
                      ...(isSmall && {
                        mt: 2.25,
                        mb: 2,
                      }),
                    }}
                  >
                    {loading ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <Skeleton
                          variant="text"
                          width={isSmall ? 150 : 300}
                          height={16}
                        />
                        <Skeleton
                          variant="text"
                          width={isSmall ? 150 : 260}
                          height={16}
                        />
                      </Box>
                    ) : (
                      <Expandable
                        actionTextColor="#4DA3E1"
                        normalTextExpand={true}
                        title="My Expanding Section"
                        description={
                          testimonials && testimonials.length > 0
                            ? testimonials[0]?.about.replace(/"/g, '')
                            : ''
                        }
                        maxCharacter={400}
                        expandLabel="See More"
                        compressLabel="See Less"
                        sx={{
                          fontSize: 12,
                          fontStyle: 'italic',
                          fontWeight: 500,
                          zIndex: 2,
                          position: 'relative',
                        }}
                      />
                    )}

                    <Image
                      src={Quotes}
                      alt="quotes"
                      width={19}
                      height={16}
                      style={{
                        position: 'absolute',
                        top: '-6px',
                        left: isSmall ? '0px' : '-6px',
                        zIndex: 1,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
        {jobInfo?.diversity && diversityId && perksAndBenefits?.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              mt: 4,
            }}
          >
            <Box
              sx={{
                flexGrow: 1,
                borderTop: '1px solid #E8ECFA',
              }}
            />

            <Typography
              variant="subtitle3"
              lineHeight={'16px'}
              sx={{
                paddingX: isSmall ? 1 : 0.75,
                backgroundColor: 'white',
              }}
            >
              CHAMPIONING WOMEN: PERKS & BENEFITS
            </Typography>

            <Box
              sx={{
                flexGrow: 1,
                borderTop: '1px solid #E8ECFA',
              }}
            />
          </Box>
        )}
        {loading ? (
          <Grid container spacing={2} px={2} mt={1}>
            {Array.from({length: isSmall ? 2 : 5}).map((_, index) => (
              <Shimmer key={index} />
            ))}
          </Grid>
        ) : (
          perksAndBenefits?.length > 0 &&
          diversityId && (
            <Box
              mt={{
                xxs: diversityId && !jobInfo?.diversity ? 3 : 1,
                sm: 1.5,
              }}
            >
              {diversityId && !jobInfo?.diversity && (
                <Box
                  display={'flex'}
                  flexDirection={{
                    xxs: 'column-reverse',
                    sm: 'row',
                  }}
                  justifyContent={'space-between'}
                  px={2}
                  gap={{
                    xxs: 2,
                    sm: 0,
                  }}
                >
                  <Typography
                    fontSize={{xxs: 12, sm: 14}}
                    fontWeight={400}
                    fontStyle={'italic'}
                    lineHeight="18px"
                    sx={{
                      ...(isSmall && {
                        textAlign: 'center',
                      }),
                    }}
                  >
                    {jobInfo?.companyData?.companyName} celebrates diversity
                    with tailored benefits!
                  </Typography>
                  <Box
                    display={'flex'}
                    gap={0.5}
                    justifyContent={'center'}
                    alignItems={'center'}
                  >
                    <Image
                      src={DiversityLogo}
                      alt="diversityLogo"
                      width={24}
                      height={24}
                    />
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      lineHeight="18px"
                      sx={{
                        background:
                          'linear-gradient(90deg, #4DA3E1 2.57%, #214FA0 24.96%, #EB633E 71.19%, #F1C659 99.91%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      DIVERSE EMPLOYER
                    </Typography>
                  </Box>
                </Box>
              )}

              <Grid
                container
                rowSpacing={{xxs: 2, sm: 2.5}}
                columnSpacing={2}
                px={{xxs: 1.5, sm: 2}}
                mt={{xxs: 1, sm: 2.5}}
                mb={perksAndBenefits && !jobInfo?.diversity ? 4 : 3}
              >
                {visiblePerks.map(
                  (item: {logo: string; title: string}, index: number) => (
                    <Grid item xs={6} md={4} lg={3} xl={2.4} key={index}>
                      <Box
                        gap={1}
                        display={'flex'}
                        flexDirection={{
                          xxs: 'column',
                          sm: 'row',
                        }}
                        alignItems={'center'}
                      >
                        <Box sx={{width: 40, height: 40}}>
                          <Box
                            sx={{
                              display: 'flex',
                              background:
                                'linear-gradient(180deg, #FFF 0%, #E8ECFA 100%)',
                              borderRadius: '50%',
                              height: '40px',
                              width: '40px',
                              position: 'relative',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Image
                              src={getDiversityCdnUrl(
                                `/assets/perkBenfitsLogo/${item.logo}.svg`
                              )}
                              alt={item.logo}
                              width={20}
                              height={20}
                            />
                          </Box>
                        </Box>

                        <Expandable
                          actionTextColor="#4DA3E1"
                          sx={{
                            lineHeight: '16px',
                            fontWeight: 500,
                            fontSize: 10,
                            textAlign: {
                              xxs: 'center',
                              sm: 'start',
                            },
                          }}
                          normalTextExpand={true}
                          title="My Expanding Section"
                          description={item?.title}
                          maxCharacter={50}
                          expandLabel="See More"
                          compressLabel="See Less"
                        />
                      </Box>
                    </Grid>
                  )
                )}

                {perksAndBenefits?.length > 4 && !expanded && (
                  <Grid
                    item
                    xs={6}
                    md={4}
                    lg={3}
                    xl={2.4}
                    container
                    justifyContent={{
                      xxs: 'center',
                      sm: 'left',
                    }}
                    alignItems="center"
                  >
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      textAlign={{
                        xxs: 'start',
                        sm: 'center',
                      }}
                    >
                      <Typography
                        onClick={toggleExpanded}
                        variant="subtitle3"
                        sx={{
                          cursor: 'pointer',
                          lineHeight: '16px',
                          textDecoration: 'none',
                          color: '#4DA3E1',
                        }}
                      >
                        {perksAndBenefits?.length <= 10
                          ? `See ${
                              perksAndBenefits?.length - (isSmall ? 3 : 4)
                            } more Benefits`
                          : `See 10+ Benefits`}
                      </Typography>
                    </Box>
                  </Grid>
                )}

                {expanded && perksAndBenefits?.length > 4 && (
                  <Grid
                    item
                    xs={6}
                    md={4}
                    lg={3}
                    xl={2.4}
                    container
                    justifyContent={{
                      xxs: 'center',
                      sm: 'left',
                    }}
                    alignItems="center"
                  >
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems={'center'}
                    >
                      <Typography
                        onClick={toggleExpanded}
                        variant="subtitle3"
                        sx={{
                          cursor: 'pointer',
                          lineHeight: '16px',
                          textDecoration: 'none',
                          color: '#4DA3E1',
                        }}
                      >
                        See Less
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>
          )
        )}
        {jobInfo?.diversity && (
          <Box
            mt={jobInfo?.diversity && perksAndBenefits ? {xxs: 4, sm: 5} : 2}
            mx={2}
            mb={3}
            display="flex"
            alignItems="center"
          >
            <Link
              textAlign="center"
              target="_blank"
              href={`${jobInfo?.diversity?.diversityUrl}`}
              fontStyle="italic"
              fontWeight={500}
              fontSize={{xxs: 12, md: 14}}
              lineHeight="18px"
              sx={{
                textDecoration: 'none',
                background:
                  'linear-gradient(90deg, #4DA3E1 2.57%, #214FA0 24.96%, #EB633E 71.19%, #F1C659 99.91%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Learn more about {jobInfo?.companyData?.companyName}&apos;s
              commitment to diversity
              <Image
                src={ChevronRight}
                alt="chevron icon"
                width={12}
                height={12}
                style={{
                  display: 'inline-block',
                  marginLeft: '4px',
                  verticalAlign: 'middle',
                }}
              />
            </Link>
          </Box>
        )}
      </Box>
    )
  );
};

export default Diversity;
